import { Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { SkillProposalRow } from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import { HELP_TEXT, RISK_HELP, SKILL_PROPOSAL_STATUS_HELP } from './agentTelemetryHelp';
import { HelpBadge, HelpLabel } from './HelpTooltip';
import SkillProposalSourceBadge from './SkillProposalSourceBadge';

interface Props {
  proposal: SkillProposalRow;
}

const riskVariant = (risk: SkillProposalRow['riskLevel']) => {
  switch (risk) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
  }
};

const statusVariant = (status: SkillProposalRow['status']) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'secondary';
    case 'revision_requested':
      return 'warning';
    case 'converted_to_shared':
      return 'info';
    case 'delivered':
      return 'primary';
    case 'pending_review':
      return 'dark';
  }
};

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '-');

const SkillProposalCard = ({ proposal }: Props) => {
  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <Card.Title className="h5 mb-1">{proposal.title}</Card.Title>
            <div className="text-muted small">
              {proposal.employeeId ? (
                <Link to={ROUTES.agentTelemetry.employeeDetail(proposal.employeeId)}>{proposal.employeeId}</Link>
              ) : (
                'Role-level proposal'
              )}
              {proposal.role ? <span className="ms-2">role={proposal.role}</span> : null}
            </div>
          </div>
          <div className="d-flex gap-1 flex-wrap justify-content-end">
            <SkillProposalSourceBadge source={proposal.proposalSource} />
            <HelpBadge bg={statusVariant(proposal.status)} description={SKILL_PROPOSAL_STATUS_HELP[proposal.status]}>
              {proposal.status}
            </HelpBadge>
            <HelpBadge bg={riskVariant(proposal.riskLevel)} description={RISK_HELP[proposal.riskLevel]}>
              risk: {proposal.riskLevel}
            </HelpBadge>
          </div>
        </div>

        <p className="mb-2">{proposal.summary}</p>

        <Row className="g-2 small mb-3">
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.proposalType}>Type</HelpLabel>
            </div>
            <div className="text-monospace">{proposal.proposalType}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.proposalSource}>Source</HelpLabel>
            </div>
            <div className="text-monospace">{proposal.proposalSource ?? 'telemetry_batch'}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.proposalTarget}>Target</HelpLabel>
            </div>
            <div className="text-monospace">
              {proposal.targetAdapter}:{proposal.targetSkillId ?? 'new-skill'}
            </div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.confidence}>Confidence</HelpLabel>
            </div>
            <div>{Math.round(proposal.confidence * 100)}%</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Safe telemetry event count and session count behind this proposal.">
                Events / sessions
              </HelpLabel>
            </div>
            <div>
              {proposal.sourceEventCount.toLocaleString()} / {proposal.sourceSessionCount.toLocaleString()}
            </div>
          </Col>
        </Row>

        <div className="p-3 rounded bg-light border mb-3">
          <div className="text-muted small mb-1">
            <HelpLabel description={HELP_TEXT.safeEvidence}>Safe evidence summary</HelpLabel>
          </div>
          <div className="small">{proposal.evidenceSummary}</div>
        </div>

        <div className="p-3 rounded border mb-3">
          <div className="text-muted small mb-1">
            <HelpLabel description={HELP_TEXT.recommendedAction}>Recommended action</HelpLabel>
          </div>
          <div className="small">{proposal.recommendedAction}</div>
        </div>

        <div className="text-muted small mb-3">
          Updated {formatDate(proposal.updatedAt)}
          {proposal.reviewerEmployeeId ? ` by ${proposal.reviewerEmployeeId}` : ''}
        </div>

        <Link to={ROUTES.agentTelemetry.proposalDetail(proposal.proposalId)} className="btn btn-outline-primary btn-sm">
          View detail
        </Link>
      </Card.Body>
    </Card>
  );
};

export default SkillProposalCard;
